<template>
  <!-- Nothing until the server says which units differ; the subtitle is a fact, not a guess -->
  <span v-if="differingIds" data-testid="drift-line">
    <span v-if="!units.length">No units yet · applies to the first unit you add</span>
    <span v-else-if="!differing.length">All {{ pluralize('unit', units.length, true) }} match</span>
    <!-- Every part is an element, so no stray whitespace lands between the names and the commas -->
    <template v-else-if="differing.length === units.length">
      <span>{{ `All ${pluralize('unit', units.length, true)} differ · ` }}</span>
      <Link :to="buildingRoute(buildingId)" :decoration="false" class="link">see units</Link>
    </template>
    <template v-else>
      <span>{{ `${units.length - differing.length} of ${pluralize('unit', units.length, true)} still match · ` }}</span>
      <template v-for="(unit, index) in differing" :key="unit.id">
        <Link :to="propertyDetailsRoute(unit.id)" :decoration="false" class="link">{{ unit.label }}</Link>
        <span v-if="index < differing.length - 1">{{ ', ' }}</span>
      </template>
      <span>{{ differing.length === 1 ? ' differs' : ' differ' }}</span>
    </template>
  </span>
</template>

<script setup lang="ts">
import pluralize from 'pluralize';
import { computed } from 'vue';

import { Link } from '@tallo/design-system';
import { buildingRoute, propertyDetailsRoute } from '@tallo/investor/navigation';
import { PropertyListItemDto, unitLabel } from '@tallo/investor/property';

/**
 * Under a default's title: how many units still carry the building's answer, and which ones have
 * moved away from it, each a link to the unit's own details.
 */
const props = defineProps<{
  buildingId: string;
  /** The ids of the units whose copy no longer matches; undefined while the server has not said */
  differingIds: string[] | undefined;
  units: PropertyListItemDto[];
}>();

const differing = computed(() =>
  props.units
    .filter((unit) => props.differingIds?.includes(unit.id))
    // By number, as the units tab orders them: "#31" in a line, "Unit 31" on the tab
    .map((unit) => ({ id: unit.id, label: unit.unitNumber ? `#${unit.unitNumber}` : unitLabel(unit) }))
    .sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true })),
);
</script>

<style scoped lang="scss">
// A name in the secondary line is a link, said quietly: text colour, a light underline
.link {
  color: var(--t-text-color-primary);
  text-decoration: underline;
  text-decoration-color: var(--t-color-border-primary);
  text-underline-offset: 0.2em;
}
</style>
